import { DateTime } from 'luxon';
import { CollectionFilteredModel, CollectionModel, customerApi, QueryTag } from '../api';
import {
  AssetInteractionModelBase,
  GeofenceProperties,
  ObjectModel,
  RemoteObjectModel,
  transformNewInvestigationResponse,
} from './interactionsApi';
import { FeatureCollection, Geometry, LineString, Point } from 'geojson';
import { AuthorModel, NameIdBasicModel, NoteModel, PostNoteQuery } from './commonModels';
import {
  InvestigationEnitityIdProps,
  registerInvestigation,
  updateInvestigationWithRawTracks,
} from '../slices/investigationsSlice';
import {
  RawTrackData,
  IRawTrackData,
  IRawTrackEntry,
  ITimestamp,
  IRawNameToId,
  IRawIdToIdAndName,
} from '@customer/protobuf/raw-tracks';
import { skipToken } from '@reduxjs/toolkit/query';
import { AssetFaultBasicModel } from './assetsApi';
import { FiltersState } from '../slices/filtersSlice';
import { getUrlParams } from './helpers';
import { InteractionState, ObjectTypeId } from '@dmc/ui';

export interface RawTrackRecord<T extends number | undefined = number> {
  id: number; // An id generated on the front-end to uniquely identify the entry.
  time: T; // GpsTime converted to milliseconds.
  assetId: T; // AssetId converted to number.
}

export interface RawTrack<T extends number | undefined = number>
  extends RawTrackRecord<T>,
    Omit<
      IRawTrackEntry,
      // Omit protobuf fields that need transformation before being stored in redux.
      | 'GpsTime'
      | 'AssetId'
      | 'SatelliteCount'
      | 'GeofenceAt'
      | 'VehiclesInView'
      // Omit protobuf fields that are not currently used.
      | 'CasWebRecieveTime'
      | 'Gear'
      | 'MessageQuality'
    > {
  satelliteCount: T; // SatelliteCount converted to number.
  geofenceAt: string[]; // GeofenceAt converted to an array of geofence names.
  vehiclesInView: string[]; // VehiclesInView converted to an array of vehicle names.
}

/** Type guard to ensure that the RawTrack generic has the required 'time' and 'assetId' properties. */
const isCompleteRawTrack = (entry: RawTrack<number | undefined>): entry is RawTrack<number> => {
  return typeof entry.time === 'number' && typeof entry.assetId === 'number';
};

export enum InvestigationStatus {
  Open = 'Open',
  VehicleIncident = 'VehicleIncident',
  ReportableIncident = 'ReportableIncident',
  Nuisance = 'Nuisance',
  BadData = 'BadData',
  Archived = 'Archived',
  NearMiss = 'NearMiss',
}

export enum InvestigationAssetType {
  Local = 'Local', // Local object
  Remote = 'Remote', // Remote object
  Nearby = 'Nearby', // Nearby object (within 50 metres of the interaction start).
}

export enum InvestigationAssetTrackCompleteness {
  None = 'None', // No track data is currently available within the time range.
  Partial = 'Partial', // Partial track data is available within the time range.
  Full = 'Full', // Track data is available for the entire time range.
  OneSecondFailed = 'OneSecondFailed', // 1 second logs failed (None)
  OneSecondRequested = 'OneSecondRequested',
  OneSecondPartial = 'OneSecondPartial',
  OneSecondFull = 'OneSecondFull',
}

export enum AssetInvestigationType {
  Unknown = 'Unknown',
  Custom = 'Custom',
  CollisionAware = 'CollisionAware',
  CollisionAlert = 'CollisionAlert',
  CollisionAlarm = 'CollisionAlarm',
  TailgatingAware = 'TailgatingAware',
  TailgatingAlert = 'TailgatingAlert',
  TailgatingAlarm = 'TailgatingAlarm',
  GeofenceSpeeding = 'GeofenceSpeeding',
  GeofenceNoGoZoneBreach = 'GeofenceNoGoZoneBreach',
  GeofenceStopSignBreach = 'GeofenceStopSignBreach',
  GeofenceStayedAtLongerThan = 'GeofenceStayedAtLongerThan',
  GeofenceStayedAtLessThan = 'GeofenceStayedAtLessThan',
  GeofenceSpeedingRoadworks = 'GeofenceSpeedingRoadworks',
}

// If the values are not available from the device, they will not be returned (undefined).
export interface ObjectScaleConfig {
  obj?: ObjectTypeId; // Equivalent to 'objectType' usage throughout the app.
  yrot?: number; // Y Rotation Offset (Usually relative to the vehicle rear, but might be different for specific object types).
  wid?: number; // Body width (metres)
  len?: number; // Body length (metres)
  boomlen?: number; // Metres (Only applicable to some object types).
  bktwid?: number; // Metres. Equivalent to boom width in beam equations. (Only applicable to some object types).
}

// Exclude unknown object types from real size compatibility types.
export type RealSizeObjectTypeId = Exclude<ObjectTypeId, ObjectTypeId.Unknown>;

export interface ObjectBodyConfig
  extends Required<Pick<ObjectScaleConfig, 'yrot' | 'wid' | 'len'>> {}

export interface RealSizeCompatible extends ObjectBodyConfig {
  obj: RealSizeObjectTypeId;
  boomlen?: ObjectScaleConfig['boomlen'];
}

export const isRealSizeCompatible = (config: ObjectScaleConfig): config is RealSizeCompatible => {
  return (
    typeof config.obj === 'number' &&
    typeof ObjectTypeId[config.obj] !== 'undefined' &&
    config.obj !== ObjectTypeId.Unknown &&
    typeof config.yrot === 'number' &&
    typeof config.wid === 'number' &&
    typeof config.len === 'number' &&
    (typeof config.boomlen === 'number' || typeof config.boomlen === 'undefined')
  );
};

export interface InvestigationAssetTrackBasicModel extends ObjectScaleConfig {
  id?: number; // Fixme: Why would the id ever be undefined?
  n: string; // Name
  asset: number; // Asset ID
  typ: InvestigationAssetType; // The type of investigation asset.
  cmp: InvestigationAssetTrackCompleteness; // The completeness of the investigation asset.
  trkd: boolean; // Whether there is any asset track data (regardless of 10s or 1s log completeness status)
  geoVer: number | null;
  geoTs: string | null;
  geoUpd: boolean | null;
  stsNam: boolean;
  hw: string; // E.g. IVUPlus
  ags: number[];
  cfgUpd: boolean | null;
  cfgTs: string | null; // Fixme: Where did this come from?
  fltCnt: number | null;
  flts: AssetFaultBasicModel[];
}

export interface InvestigationBasicModel {
  id: number; // Investigation ID.
  name?: string | null;
  ts: string; // Timestamp of interaction.
  st: InvestigationStatus; // Investigation status.
  it: AssetInvestigationType; // Investigation type.
  createdBy: AuthorModel; // User who started the investigation.
  aupd: string; // Timestamp of last asset update? Fixme: Double-check what this field is.
  assets: InvestigationAssetTrackBasicModel[]; // The assets involved in the interaction (or nearby).
}

export interface InvestigationModel extends InvestigationBasicModel {
  createdAt: string; // Timestamp of investigation creation.
  iid: string | null; // Interaction ID
  dur: number | null; // Duration, in seconds.
  dist: number | null; // Distance, in metres. Will be null for Custom investigations.
  aud: boolean | null; // Audible
  rn: string | null; // Rule Name
  act: boolean; // active
  geo?: NameIdBasicModel | null; // Fixme: This is present in the response, but not currently used?
  lo: ObjectModel | null; // local object
  ro: RemoteObjectModel | null; // remote object
  svfix: boolean | null; // Valid GPS fix at start of event. // Fixme: Why is svgfix nullable but evfix undefined?
  evfix?: boolean; // Valid GPS fix at end of event. Fixme: Will this receive null?
  siteGeoTs?: string; // Site geofence timestamp
  siteGeoVer?: number; // Site geofence version
  siteGeoCfg: FeatureCollection<Geometry, GeofenceProperties> | null;
  loGeoCfg: FeatureCollection<Geometry, GeofenceProperties> | null;
  tsStart: string; // start of investigation time range
  tsEnd: string; // end of investigation time range
  mode?: AnalysisMode; // The analysis mode of the investigation, either 'Basic' or 'Enhanced'.
  oneSecLog: boolean; // one-second logging enabled
}

// Extend a subset of the AssetInteractionModelBase to verify compatibility.
export interface LoggedInvestigationModel
  extends InvestigationModel,
    Pick<
      AssetInteractionModelBase,
      'ts' | 'dur' | 'dist' | 'aud' | 'act' | 'rn' | 'svfix' | 'evfix' | 'lo'
    > {
  it: Exclude<
    AssetInvestigationType,
    AssetInvestigationType.Custom | AssetInvestigationType.Unknown
  >;
  iid: string; // Always present
  dist: number; // Always present
  aud: boolean; // Always present
  svfix: boolean; // Always present
  evfix: boolean; // Always present
  lo: ObjectModel; // Always present
}

export interface CustomInvestigationModel extends InvestigationModel {
  it: AssetInvestigationType.Custom;
  iid: null; // No associated interaction.
  name: string; // Must have a name
  dist: null; // No associated distance
  aud: null; // No assocated audible state
  rn: null; // No associate rule
  svfix: null; // no associated event
  geo?: null; // Fixme: Is this typed correctly?
  lo: null; // Only nearby assets
  ro: null; // Only nearby assets
  mode: AnalysisMode;
}

export interface InvestigationStatusUpdateModel {
  status: InvestigationStatus;
  noteText?: string;
}

interface InvestigationStatusUpdateQuery extends InvestigationStatusUpdateModel {
  investigationId: string;
}

interface AreaModel<T extends number | null = number | null> {
  sLat: T; // Start latitude (North)
  sLon: T; // Start longitude (West)
  eLat: T; // End latitude (South)
  eLon: T; // End longitude (East)
}

// The IDs of the lines should match these enum members.
export enum AssetLineSegment {
  Before = 1,
  During = 2,
  After = 3,
}

export enum AssetLineStyle {
  DuringInteraction = 1,
  NotDuringInteraction = 0,
}

export interface LineProperties {
  st: AssetLineStyle; // Style of the line segment based on being inside/outside the interaction timeframe.
}

export enum PointEventType {
  Other = 0,
  Start = 1,
  End = 2,
}

export enum MotiveState {
  DEENG_SECURE = 0, // Ignition is off, ParkBrake is on
  DEENG_NOBRAKE = 1, // Ignition is off, Parkbrake is off (or there's no ParkBrake)
  ENG_SECURE = 2, // Ignition is on, ParkBrake is on (or there isn't one), NEUTRAL gear
  ENG_NEUTRAL = 3, // -
  ENG_FORWARD = 4, // Ignition is on, Forward gear, not moving reverse
  ENG_REVERSE = 5, // Ignition is on, Reverse gear, not moving forward
  ENG_ANY = 6, // This is NORMAL for tracked vehciles.
  UNKNOWN = 7, // Error state
}

export interface ArcBeamData {
  // Inner beam (alarm)
  AlarmArcLength: number; // End angle (degrees) NOT arc length.
  AlarmBeamRadius: number; // metres
  AlarmStartAngle: number; // degrees
  // Outer beam (warn)
  WarnArcLength: number; // End angle (degrees) NOT arc length.
  WarnBeamRadius: number; // metres
  WarnStartAngle: number; // degrees
}

export interface CurveBeamData {
  // Inner beam (alarm)
  AlarmBeamDW: number;
  AlarmBeamBuffer: number;
  AlarmBeamLength: number;
  // Outer beam (warn)
  WarnBeamDW: number;
  WarnBeamBuffer: number;
  WarnBeamLength: number;
  // Both
  RearBeamEnabled: boolean;
  FrontBeamEnabled: boolean;
  CurvedBeamEnabled: boolean;
}

// Either Arc or Curved will have data - not both.
export interface BasicBeamData {
  Arc: ArcBeamData | null;
  Curved: CurveBeamData | null;
  CurveRadius: number | null; // float
  CourseOverGround: number | null; // float
  HeadingValid: boolean | null; // Mixed heading valid
  InteractionState: InteractionState | null;
}

export interface ValidArcBeamData extends BasicBeamData {
  Arc: ArcBeamData;
  Curved: null;
  InteractionState: InteractionState;
}

export interface ValidCurveBeamData extends BasicBeamData {
  Arc: null;
  Curved: CurveBeamData;
  CurveRadius: number;
  InteractionState: InteractionState;
}

export interface PointProperties {
  ts: string; // Timestamp of the point.
  hdg: number | null; // Heading of the asset.
  spd: number; // Asset speed (metres per second).
  evttyp: number; // Event type (integer). Not currently being used for anything.
  pttyp: PointEventType; // The simplified event type.
  beamdata?: BasicBeamData | null; // Will not be present if not provided by the device.
}

export interface TrackModel extends AreaModel, InvestigationAssetTrackBasicModel {
  line: FeatureCollection<LineString, LineProperties> | null; // Line GeoJson feature collection with custom properties.
  points: FeatureCollection<Point, PointProperties> | null; // Points GeoJson feature collection with custom properties.
}

interface TracksResponse extends AreaModel {
  tracks: TrackModel[];
}

interface TransformedTracksResponse extends TracksResponse {
  queryKey: string;
}

interface ValidProtobufTimestamp extends ITimestamp {
  seconds: number | Long; // Whole seconds.
  nanos: number; // The remainder in nanoseconds.
}

/** Type guard to confirm that all of the ITimestamp properties are set without nullish values. */
const isValidProtobufTimestamp = (value: ITimestamp): value is ValidProtobufTimestamp => {
  const { seconds, nanos } = value;
  return (
    seconds !== null &&
    typeof seconds !== 'undefined' &&
    'nanos' in value &&
    nanos !== null &&
    typeof nanos !== 'undefined'
  );
};

const protobufTimestampToMillis = ({ seconds, nanos }: ValidProtobufTimestamp): number => {
  const wholeSeconds: number = typeof seconds === 'number' ? seconds : seconds.toInt();
  const partialSecond: number = nanos / 1e9;
  const millis = Math.round((wholeSeconds + partialSecond) * 1e3);
  return millis;
};

const getNumberFromOptionalLong = (value: number | Long | null | undefined): number | undefined => {
  return typeof value === 'number' ? value : value ? value.toInt() : undefined;
};

/**
 * Convert the IRawIdToIdAndName object array to a list of truthy geofence names.
 * The RawId and Id properties are not currently in use, so they can be excluded without Long conversion.
 * Since the IRawIdToIdAndName interface properties are all optional, any array of objects will pass.
 */
const getGeofenceAt = (GeofenceAt: IRawIdToIdAndName[] | null | undefined): string[] => {
  const names: string[] = [];
  if (Array.isArray(GeofenceAt)) {
    for (let i = 0; i < GeofenceAt.length; i++) {
      const name = GeofenceAt[i]?.Name;
      if (name) {
        names.push(name);
      }
    }
  }
  return names;
};

/**
 * Convert the IRawNameToId object array to a list of truthy vehicle names.
 * The Id property is not currently in use.
 * Since the IRawNameToId interface properties are all optional, any array of objects will pass.
 */
const getVehiclesInView = (VehiclesInView: IRawNameToId[] | null | undefined): string[] => {
  const names: string[] = [];
  if (Array.isArray(VehiclesInView)) {
    for (let i = 0; i < VehiclesInView.length; i++) {
      const name = VehiclesInView[i]?.RawName;
      if (name) {
        names.push(name);
      }
    }
  }
  return names;
};

/** Convert IRawTrackEntry[] to RawTrack[]. */
const getRawTracks = (trackEntries: IRawTrackEntry[]): RawTrack[] => {
  const interimRecords = trackEntries.map(
    (entry: IRawTrackEntry, idx: number): RawTrack<number | undefined> => {
      const {
        // Extract fields that require transformation before being stored in redux.
        GpsTime,
        AssetId,
        SatelliteCount,
        GeofenceAt,
        VehiclesInView,
        // Remove unused fields from all entries.
        CasWebRecieveTime,
        Gear,
        MessageQuality,
        ...rest
      } = entry;

      // Convert GpsTime, AssetId, SatelliteCount to native types.
      const time: number | undefined =
        GpsTime && isValidProtobufTimestamp(GpsTime)
          ? protobufTimestampToMillis(GpsTime)
          : undefined;
      const assetId = getNumberFromOptionalLong(AssetId);
      const satelliteCount = getNumberFromOptionalLong(SatelliteCount);

      /**
       * Convert GeofenceAt, VehiclesInView to a list of strings.
       * Note: IRawTrackEntry and IRawNameToId are both interfaces with no required properties.
       * In practice, this means that any array of objects will satisfy either of the interfaces.
       * There is no workaround, other than introducing an expensive type guard.
       * This type awareness gap leads to the loss of features if the wrong field is provided,
       * but it does not lead to a loss of type safety.
       */
      const geofenceAt = getGeofenceAt(GeofenceAt);
      const vehiclesInView = getVehiclesInView(VehiclesInView);

      return {
        ...rest,
        id: idx + 1, // Give each entry a unique ID.
        time,
        assetId,
        satelliteCount,
        geofenceAt,
        vehiclesInView,
      };
    },
  );

  // Make sure that all entries have a valid shape.
  const rawTracks = interimRecords.filter(isCompleteRawTrack);

  return rawTracks;
};

export type InvestigationsQueryParams = Partial<
  Pick<FiltersState, 'investigationTypes' | 'investigationStatuses'>
>;

export enum AnalysisMode {
  Basic = 'Basic',
  Enhanced = 'Enhanced',
}

export interface CreateCustomInvestigationModel {
  n: string; // Name (required, 1-50 characters).
  start: string; // start time (UTC Timestamp, required, not more than 94 days ago).
  end: string; // end time (UTC Timestamp, required, after start but not more than 4 hours after start).
  mode: AnalysisMode; // Required, default should be 'Basic'.
  oneSecLog: boolean; // Only allowed to be true if the analysis mode is 'enhanced'.
  assets: number[]; // Array of asset IDs (long[], required, 1-5 length, must exist and be in current site).
}

// The transformed response will be the ID of the newly created investigation (if successful).
export type CreateInvestigationResponse = number | null;

export interface EditLoggedInvestigationModel {
  investigationId: string; // Numerical ID
  n: string;
  oneSecLog: boolean;
}

export interface EditCustomInvestigationModel extends CreateCustomInvestigationModel {
  investigationId: string; // Numerical ID
}

export const investigationsApi = customerApi.injectEndpoints({
  endpoints: build => ({
    getInvestigations: build.query<
      CollectionFilteredModel<InvestigationBasicModel>,
      InvestigationsQueryParams
    >({
      query: query => ({
        url: 'api/investigations',
        params: getUrlParams(query),
      }),
      providesTags: (result, error, arg) =>
        result ? [QueryTag.SiteCache, { type: QueryTag.Investigation, id: 'LIST' }] : [],
    }),
    getInvestigation: build.query<InvestigationModel, InvestigationEnitityIdProps>({
      query: ({ investigationId }) => `api/investigations/${investigationId}`,
      providesTags: (result, error, { investigationId }) =>
        result ? [QueryTag.SiteCache, { type: QueryTag.Investigation, id: investigationId }] : [],
      async onQueryStarted(
        { siteId, investigationId },
        { dispatch, queryFulfilled },
      ): Promise<void> {
        try {
          const {
            data: { ts, assets },
          } = await queryFulfilled;
          // Create or update the investigation enitity with the interaction timestamp and list of assets.
          dispatch(
            registerInvestigation({
              siteId,
              investigationId,
              data: { ts, assets },
            }),
          );
        } catch (error) {
          console.debug(error);
        }
      },
    }),
    getTracks: build.query<TransformedTracksResponse, string>({
      query: investigationId => `api/investigations/${investigationId}/tracks`,
      transformResponse: (response: TracksResponse): TransformedTracksResponse => ({
        ...response,
        // This queryKey is used to trigger a re-render of the investigation map when the response returns.
        queryKey: DateTime.now().toMillis().toString(),
      }),
      providesTags: (result, error, investigationId) =>
        result
          ? [QueryTag.SiteCache, { type: QueryTag.InvestigationTracks, id: investigationId }]
          : [],
    }),
    getNotes: build.query<CollectionModel<NoteModel>, string>({
      query: investigationId => `api/investigations/${investigationId}/notes`,
      providesTags: (result, error, investigationId) =>
        result
          ? [QueryTag.SiteCache, { type: QueryTag.InvestigationNotes, id: investigationId }]
          : [],
    }),
    postNote: build.mutation<void, PostNoteQuery>({
      query: ({ id, ...body }) => ({
        url: `api/investigations/${id}/notes`,
        method: 'POST',
        body,
      }),
      invalidatesTags: (result, error, { id }) =>
        !error ? [{ type: QueryTag.InvestigationNotes, id: id }] : [],
    }),
    postStatus: build.mutation<void, InvestigationStatusUpdateQuery>({
      query: ({ investigationId, ...body }) => ({
        url: `api/investigations/${investigationId}/status`,
        method: 'POST',
        body,
      }),
      invalidatesTags: (result, error, { investigationId }) =>
        !error
          ? [
              { type: QueryTag.InvestigationNotes, id: investigationId },
              { type: QueryTag.Investigation, id: investigationId },
              { type: QueryTag.Investigation, id: 'LIST' },
            ]
          : [],
    }),
    getRawTracks: build.query<RawTrack[], InvestigationEnitityIdProps>({
      query: ({ investigationId }) => ({
        url: `api/investigations/${investigationId}/rawtracks`,
        // Specify how to handle the non-JSON response.
        responseHandler: (response: Response) => (response.ok ? response.blob() : response.json()),
      }),
      transformResponse: async (response: Blob): Promise<RawTrack[]> => {
        try {
          let entries: RawTrack[] = [];

          // Convert Blob to usable buffer.
          const buffer: Uint8Array<ArrayBuffer> = new Uint8Array(await response.arrayBuffer());

          // Decode the buffer.
          const decodedMessage = RawTrackData.decode(buffer);

          // Note: The toObject method might need to be provided additional options to populate the nested/repeated fields with defaults.
          const dataAsObject: IRawTrackData = RawTrackData.toObject(decodedMessage, {
            defaults: true,
          });

          if (dataAsObject.TrackEntries) {
            entries = getRawTracks(dataAsObject.TrackEntries);
          }

          return entries;
        } catch (error) {
          console.debug('Error decoding raw track data:', error);
          return [];
        }
      },
      providesTags: (result, error, { investigationId }) =>
        result
          ? [QueryTag.SiteCache, { type: QueryTag.InvestigationRawTracks, id: investigationId }]
          : [],
      async onQueryStarted(
        { siteId, investigationId },
        { dispatch, queryFulfilled },
      ): Promise<void> {
        try {
          const { data: rawTracks } = await queryFulfilled;

          const rawTrackRecords: RawTrackRecord[] = rawTracks.map(
            ({ id, assetId, time }: RawTrack) => ({ id, assetId, time }),
          );

          // Update the investigation entity with the rawtrack data.
          dispatch(
            updateInvestigationWithRawTracks({
              siteId,
              investigationId,
              rawTrackRecords,
            }),
          );
        } catch (error) {
          console.debug(error);
        }
      },
    }),
    createInvestigation: build.mutation<
      CreateInvestigationResponse,
      CreateCustomInvestigationModel
    >({
      query: body => ({
        url: 'api/investigations',
        method: 'POST',
        body,
      }),
      transformResponse: transformNewInvestigationResponse,
      invalidatesTags: (result, error, args) =>
        !error ? [{ type: QueryTag.Investigation, id: 'LIST' }] : [],
    }),
    editInvestigation: build.mutation<
      void,
      EditLoggedInvestigationModel | EditCustomInvestigationModel
    >({
      query: ({ investigationId, ...body }) => ({
        url: `api/investigations/${investigationId}`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: (result, error, { investigationId }) =>
        !error
          ? [
              { type: QueryTag.Investigation, id: 'LIST' },
              { type: QueryTag.Investigation, id: investigationId },
              { type: QueryTag.InvestigationNotes, id: investigationId },
              { type: QueryTag.InvestigationTracks, id: investigationId },
              { type: QueryTag.InvestigationRawTracks, id: investigationId },
            ]
          : [],
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetInvestigationsQuery,
  useGetInvestigationQuery,
  useGetNotesQuery,
  useGetTracksQuery,
  useGetRawTracksQuery,
  usePostNoteMutation,
  usePostStatusMutation,
  useCreateInvestigationMutation,
  useEditInvestigationMutation,
} = investigationsApi;

interface UseGetRawTracksReturn {
  investigation: InvestigationModel | undefined;
  rawTracks: RawTrack[] | undefined;
}

export const useGetRawTracks = ({
  siteId,
  investigationId,
}: Partial<InvestigationEnitityIdProps>): UseGetRawTracksReturn => {
  const isSkipQuery = typeof investigationId === 'undefined' || typeof siteId === 'undefined';

  const { currentData: investigation } = useGetInvestigationQuery(
    isSkipQuery ? skipToken : { siteId, investigationId },
    { refetchOnMountOrArgChange: 60 },
  );

  const isSkipRawTracksQuery = isSkipQuery || !investigation;

  // The raw tracks query must only ever be called after the 'useGetInvestigationQuery' has succeeded at least once.
  // When this query is fulfilled, it will load the data that the playback feature relies on into the store.
  const { currentData: rawTracks } = useGetRawTracksQuery(
    isSkipRawTracksQuery ? skipToken : { siteId, investigationId },
    { refetchOnMountOrArgChange: 60 },
  );

  return { investigation, rawTracks };
};
